import { Icon, Input } from "@deligoo/ui"
import clsx from "clsx"
import dayjs from "dayjs"
import { debounce } from "lodash"
import { memo, useEffect, useRef, useState } from "react"
import { batch } from "react-redux"
import { useHistory } from "react-router-dom"
import { useClickAway } from "react-use"

import { useAppDispatch, useAppSelector } from "@/App"
import { setDate } from "@/store/global"
import { selectCurrentTeamId, selectTeams, setCurrentTeam } from "@/store/teams"
import { setAboutPanel, setShowDriverDetails, setShowOrderDetails } from "@/store/ui"
import { fetchSearchOrders } from "@/utils"

import cls from "./OrdersSearch.module.scss"

export type OrdersSuggestions = Array<{
  client_id: number
  id: number
  pickup_at: string | null
  driver_id: number | null
  team_id: number
  uid: string
}>

type OrdersSearchProps = {
  handleClose: () => void
}

export const OrdersSearch = memo(({ handleClose }: OrdersSearchProps) => {
  const currentTeamId = useAppSelector(selectCurrentTeamId)
  const currentSelectedDate = useAppSelector((state) => state.global.date)
  const teams = useAppSelector(selectTeams)

  const [searchValue, setSearchValue] = useState("")
  const [suggestions, setSuggestions] = useState<OrdersSuggestions>([])
  const [showSuggestions, setShowSuggestions] = useState(false)

  const ordersSearchRef = useRef<HTMLDivElement | null>(null)

  const dispatch = useAppDispatch()

  const history = useHistory()

  useClickAway(ordersSearchRef, () => setShowSuggestions(false))

  function selectSuggestion(suggestion: OrdersSuggestions[number]) {
    history.push("/")

    batch(() => {
      const team = teams.find((team) => team.id === suggestion.team_id)
      const shouldSetDate = dayjs(suggestion.pickup_at).isBefore(currentSelectedDate)

      if (shouldSetDate) dispatch(setDate(dayjs(suggestion.pickup_at).format("YYYY-MM-DD")))
      if (team && currentTeamId !== team.id) dispatch(setCurrentTeam(team))
    })

    batch(() => {
      if (suggestion.driver_id) dispatch(setShowDriverDetails(suggestion.driver_id))
      dispatch(setShowOrderDetails({ id: suggestion.id, type: "pickup" }))
      dispatch(setAboutPanel({ type: "order", id: suggestion.id }))
      setShowSuggestions(false)
    })
  }

  const getSuggestions = useRef(
    debounce(async (orderUid: string) => {
      if (orderUid.length < 2) return

      const { json } = await fetchSearchOrders(orderUid)

      if (json.data) {
        setSuggestions(json.data)
        setShowSuggestions(true)
      }
    }, 500)
  ).current

  useEffect(() => {
    getSuggestions(searchValue)

    return () => {
      getSuggestions.cancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue])

  return (
    <div className={cls.ordersSearch} ref={ordersSearchRef}>
      <Input
        value={searchValue}
        onChange={(e) => setSearchValue(e.currentTarget.value)}
        onFocus={() => setShowSuggestions(true)}
        placeholder="Wyszukaj zlecenia"
        className={cls.input}
        autoFocus
        dark
        plain
      />

      <button onClick={handleClose} className={cls.close} title="Zamknij" type="button">
        <Icon name="close-tiny" className={cls.icon} />
      </button>

      {suggestions.length > 0 && showSuggestions && (
        <ul className={cls.results}>
          {suggestions.map((suggestion) => (
            <li
              onClick={(e) => selectSuggestion(suggestion)}
              className={clsx(cls.item, !suggestion.pickup_at && cls.disabled)}
              key={suggestion.id}
              role="button"
            >
              <div>
                <div className={cls.id}>#{suggestion.uid}</div>
                <div className={cls.meta}>Zespół {teams.find((team) => team.id === suggestion.team_id)?.name}</div>
              </div>

              <div className={cls.date}>
                {suggestion.pickup_at ? (
                  <>
                    <Icon name="calendar" color="muted" size="small" />
                    {dayjs(suggestion.pickup_at).format("DD.MM.YYYY")}
                  </>
                ) : (
                  "-"
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
})
